import { useCallback } from 'react';
import { CalendarEvent } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/calendar_event.pb';
import { Provider } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/provider.pb';
import { Schedule } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/schedule.pb';
import { uniqBy } from 'lodash-es';
import { ProviderEventType } from '../../components/calendar-view/types';
import { getFullName } from '../../utils';
import { useGetCalendarEventsV3 } from './use-get-calendar-events-v3';

type UseGetPractitionerDetailsForCalendarV3Params = {
  calendarEvents: CalendarEvent[];
  practitioners: Provider[];
  schedules: Schedule[];
};

export const useGetPractitionerDetailsForCalendarV3 = ({
  calendarEvents,
  practitioners,
  schedules,
}: UseGetPractitionerDetailsForCalendarV3Params) => {
  const {
    getPractitionerCalendarEvents,
    getEntityWorkingHours,
    getEntityAvailabilityExceptions,
    getEntityExceptionEvents,
  } = useGetCalendarEventsV3();

  const getPractitionerEventsForCalendarObject = useCallback(
    (locationId: string): ProviderEventType[] => {
      return (
        practitioners
          .filter(({ locationId: providerLocationId }) => providerLocationId === locationId)
          .map<ProviderEventType | null>((practitioner) => {
            const practitionerId = practitioner.id ?? '';

            const calendarEventList = getPractitionerCalendarEvents({
              calendarEvents,
              locationId,
              practitionerId,
            });

            // if provider is archived then show them if they have events or if they are not hidden
            const providerVisible = !practitioner?.profile?.act ? !!calendarEventList.length : !practitioner.hidden;
            if (!practitionerId || !providerVisible) {
              return null;
            }

            const workingHours = getEntityWorkingHours({
              schedules,
              locationId,
              entityId: practitionerId,
            });

            const availabilityExceptions = getEntityAvailabilityExceptions({
              schedules,
              locationId,
              entityId: practitionerId,
            });

            const exceptionEventList = getEntityExceptionEvents({
              schedules,
              locationId,
              entityId: practitionerId,
            });

            return {
              name: getFullName(practitioner),
              providerId: practitionerId,
              src: practitioner.profile?.picUrl ?? practitioner.displayName ?? '',
              events: uniqBy([...calendarEventList, ...exceptionEventList], (event) => event.eventId),
              hasAnyWorkingHoursSet: !!schedules.find(
                (schedule) => schedule.locationId === locationId && schedule.id === practitionerId
              )?.recurrenceRules?.workingHours?.length,
              workingHours,
              availabilityExceptions,
              archived: !practitioner?.profile?.act,
            };
          })
          .filter((p) => !!p) ?? []
      );
    },
    [calendarEvents, practitioners, schedules]
  );

  return { getPractitionerEventsForCalendarObject };
};
